import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../services/api';
import { getSupportedAudioRecorderFormat } from '../../utils/mediaRecorderUtils';
import './VoiceRecorder.css';

const MIN_RECORD_SECONDS = 10; // Onboarding voice sample (voice cloning requires 30s separately)

function VoiceRecorder({ onComplete, onNext }) {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [voiceConsent, setVoiceConsent] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      setError(null);
      const { mimeType, extension } = getSupportedAudioRecorderFormat();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blobType = mediaRecorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: blobType });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Error starting recording:', err);
      setError(t('onboarding.voice.error_permission'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const handleUpload = async () => {
    if (!audioBlob || !voiceConsent) {
      if (!voiceConsent) {
        setError(t('onboarding.voice.consent_required'));
      } else if (recordingTime < MIN_RECORD_SECONDS) {
        setError(t('onboarding.voice.min_duration', { seconds: MIN_RECORD_SECONDS }));
      }
      return;
    }

    if (recordingTime < MIN_RECORD_SECONDS) {
      setError(t('onboarding.voice.min_duration', { seconds: MIN_RECORD_SECONDS }));
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      const { extension } = getSupportedAudioRecorderFormat();
      const formData = new FormData();
      formData.append('voice_file', audioBlob, `voice.${extension}`);

      await apiClient.post('/api/v1/onboarding/voice', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onComplete();
      onNext();
    } catch (err) {
      console.error('Error uploading voice:', err);
      setError(t('onboarding.voice.error_upload'));
    } finally {
      setUploading(false);
    }
  };

  const handleReRecord = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setError(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="voice-recorder">
      <div className="voice-header">
        <h2>{t('onboarding.voice.title')}</h2>
        <p>{t('onboarding.voice.description')}</p>
      </div>

      {!audioUrl ? (
        <div className="voice-recording-section">
          <div className="recording-instructions">
            <p>{t('onboarding.voice.instructions')}</p>
          </div>

          <div className="recording-controls">
            {!isRecording ? (
              <button onClick={startRecording} className="record-button">
                <span className="record-icon">🎤</span>
                {t('onboarding.voice.record_button')}
              </button>
            ) : (
              <div className="recording-active">
                <div className="recording-indicator">
                  <div className="pulse"></div>
                  <span>{t('onboarding.voice.recording')}</span>
                </div>
                <div className="recording-time">{formatTime(recordingTime)}</div>
                <button onClick={stopRecording} className="stop-button">
                  {t('onboarding.voice.stop_button')}
                </button>
              </div>
            )}
          </div>

          <div className="voice-consent">
            <label className="consent-checkbox">
              <input
                type="checkbox"
                checked={voiceConsent}
                onChange={(e) => setVoiceConsent(e.target.checked)}
              />
              <span>{t('onboarding.voice.consent_text')}</span>
            </label>
          </div>
        </div>
      ) : (
        <div className="voice-preview-section">
          <h3>{t('onboarding.voice.preview_title')}</h3>
          <audio src={audioUrl} controls className="audio-preview" />
          
          <div className="voice-consent">
            <label className="consent-checkbox">
              <input
                type="checkbox"
                checked={voiceConsent}
                onChange={(e) => setVoiceConsent(e.target.checked)}
              />
              <span>{t('onboarding.voice.consent_text')}</span>
            </label>
          </div>

          {recordingTime < MIN_RECORD_SECONDS && (
            <div className="warning-message">
              {t('onboarding.voice.min_duration', { seconds: MIN_RECORD_SECONDS })}
            </div>
          )}

          <div className="preview-actions">
            <button onClick={handleReRecord} className="re-record-button">
              {t('onboarding.voice.re_record_button')}
            </button>
            <button 
              onClick={handleUpload} 
              className="continue-button"
              disabled={uploading || !voiceConsent || recordingTime < MIN_RECORD_SECONDS}
            >
              {uploading ? t('onboarding.voice.uploading') : t('onboarding.voice.continue_button')}
            </button>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );
}

export default VoiceRecorder;

